// src/pages/accounting/OfficeExpensesPage.jsx
// Extracted from App.js - Office Expenses admin page
// BRIO Migration: Using code "1b" (table compact)
// CURRENCY: Per-expense INR/EUR input toggle + display currency from useSettings
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Trash2, Upload, ChevronDown, ChevronRight, AlertTriangle, X } from 'lucide-react'; // eslint-disable-line no-unused-vars
import {
  BrioAnalyticsSurface,
  BrioFrame,
  BrioByCode,
  BrioTable,
  BrioIconButton,
  BrioMonthPicker,
  BrioDatePicker,
  BrioSelect,
  getTablePropsFromCode
} from '../../components/BrioComponents';
import { useSettings } from '../../hooks/useSettings';
import { OFFICE_OVERHEAD_CATEGORIES, canonicalizeOfficeCategory, looksLikeBankStatement } from '../../utils/expenseShared';

// Presentation code for this page
const PRESENTATION_CODE = '1b';

const CATEGORY_ORDER = OFFICE_OVERHEAD_CATEGORIES;

// Display-only category label overrides. The stored category KEY is left
// unchanged — it's a data value in office_overheads_yearly and drives the
// employer-burden / profitability calculations (e.g. Pension + "Salary Tax").
// Only the user-facing text differs here.
const CATEGORY_LABEL_OVERRIDES = { 'Salary Tax': 'Tax' };
const categoryLabel = (cat) => CATEGORY_LABEL_OVERRIDES[cat] || cat;

// ── Inline currency toggle (compact, BRIO gold) ──────────────────────────
function CurrencyToggle({ value = 'INR', onChange, style }) {
  const btn = (c) => ({
    border: 'none', cursor: 'pointer', fontFamily: "'Raleway', sans-serif",
    fontWeight: 600, fontSize: 10, padding: '2px 7px', lineHeight: 1.4,
    letterSpacing: '0.02em', transition: 'background 0.15s, color 0.15s',
    background: value === c ? 'var(--brio-gold, #c5a572)' : '#fff',
    color: value === c ? '#fff' : '#888',
  });
  return (
    <div style={{ display: 'inline-flex', borderRadius: 4, overflow: 'hidden', border: '1px solid var(--brio-border, #d4d0c8)', flexShrink: 0, ...style }}>
      <button type="button" style={btn('INR')} onClick={() => onChange('INR')} title="Indian Rupees">₹</button>
      <button type="button" style={btn('EUR')} onClick={() => onChange('EUR')} title="Euros">€</button>
    </div>
  );
}

// ── CSV parser: handles tab-separated and comma-separated ────────────────
function parseCsvRows(text) {
  const lines = text.trim().split('\n').filter(l => l.trim());
  if (lines.length === 0) return [];

  // Detect separator: if first line has tabs, use tabs; otherwise commas
  const sep = lines[0].includes('\t') ? '\t' : ',';

  return lines.map((line, idx) => {
    const cols = line.split(sep).map(c => c.trim());
    // Expected format: Category, Subcategory, Amount [, Office] [, Currency]
    // Minimum: 3 columns
    if (cols.length < 3) return { error: `Row ${idx + 1}: need at least 3 columns (Category, Subcategory, Amount)`, raw: line };

    const category = cols[0];
    const subcategory = cols[1];
    const amount = parseFloat(cols[2]);

    if (!category) return { error: `Row ${idx + 1}: empty category`, raw: line };
    if (!subcategory) return { error: `Row ${idx + 1}: empty subcategory`, raw: line };
    if (isNaN(amount) || amount <= 0) return { error: `Row ${idx + 1}: invalid amount "${cols[2]}"`, raw: line };

    // Optional columns
    const office = cols[3] || null;    // per-row office override
    const currency = cols[4] || null;  // per-row currency override

    return { category, subcategory, amount, office, currency, raw: line };
  });
}

export default function OfficeExpensesPage({
  // Data
  activeOfficeOverheads,
  officeOverheads,
  setOfficeOverheads,
  // Form state
  form,
  setForm,
  // Supabase
  useSupabaseDB,
  supabaseOfficeOverheads,
  // Expenses hook — bank-imported office expenses live here
  supabaseExpenses,
  // Formatters (legacy — kept for signature compat)
  fmtCurr,
  currSymbol = '₹',
}) {
  const tableProps = getTablePropsFromCode(PRESENTATION_CODE);
  const [sortConfig, setSortConfig] = useState({ key: 'category', direction: 'asc' });

  // ── Filters (persisted to localStorage) ──
  const _loadOEFilters = () => { try { return JSON.parse(localStorage.getItem('brio_office_expenses_filters') || '{}'); } catch { return {}; } };
  const [filterYear,       setFilterYear]       = useState(() => _loadOEFilters().year       ?? String(new Date().getFullYear()));
  const [filterDateFrom,   setFilterDateFrom]   = useState(() => _loadOEFilters().dateFrom   || '');
  const [filterDateTo,     setFilterDateTo]     = useState(() => _loadOEFilters().dateTo     || '');
  const [filterSourceType, setFilterSourceType] = useState(() => _loadOEFilters().sourceType || '');
  const [filterCategory,   setFilterCategory]   = useState(() => _loadOEFilters().category   || '');
  const [filterOffice,     setFilterOffice]     = useState(() => _loadOEFilters().office     || '');

  useEffect(() => {
    try { localStorage.setItem('brio_office_expenses_filters', JSON.stringify({ year: filterYear, dateFrom: filterDateFrom, dateTo: filterDateTo, sourceType: filterSourceType, category: filterCategory, office: filterOffice })); } catch {}
  }, [filterYear, filterDateFrom, filterDateTo, filterSourceType, filterCategory, filterOffice]);

  // ── Category drawer state (collapsed by default) ──
  const [expandedCategories, setExpandedCategories] = useState({});

  // ── Import state ──
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState('');
  const [importCurrency, setImportCurrency] = useState('INR');
  const [importOffice, setImportOffice] = useState('NL');
  const [importRows, setImportRows] = useState(null); // parsed preview rows
  const [importErrors, setImportErrors] = useState([]);
  const [importing, setImporting] = useState(false);

  // ── Currency settings from useSettings hook (direct call — self-sufficient) ──
  const settings = useSettings();
  const displayCurrency = settings.displayCurrency || 'INR';
  const getFxRate = settings.getFxRateForDate;
  const convertMoney = settings.convertMoney;
  const formatMoney = settings.formatMoney || fmtCurr || ((v) => String(v));
  const fxRateMap = settings.fxRateMap || new Map();

  const displaySymbol = displayCurrency === 'EUR' ? '€' : '₹';

  // ── Currency input state for form row ──
  const inputCurrency = form.newOverheadCurrency || 'INR';
  const currentYear = Number(filterYear) || new Date().getFullYear();

  // Month for FX rate lookup — user can pick, defaults to current month of selected year
  const defaultFxMonth = (() => {
    const now = new Date();
    if (currentYear === now.getFullYear()) return `${currentYear}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    return `${currentYear}-01`;
  })();
  const fxMonthKey = form.newOverheadFxMonth || defaultFxMonth;

  // FX helper text for form row (monthly average)
  const fxHelper = useMemo(() => {
    if (inputCurrency === 'INR') return null;
    if (!getFxRate) return { text: 'Currency settings not loaded yet', warn: true };
    const [y, m] = fxMonthKey.split('-');
    const label = new Date(+y, +m - 1).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
    const rate = getFxRate(fxMonthKey + '-01');
    if (!rate || rate <= 1) return { text: `No valid FX rate found for ${label}. Add rates in Settings → FX rates.`, warn: true };
    const isExact = fxRateMap.has(fxMonthKey);
    if (isExact) {
      return { text: `Avg rate ${label}: 1 EUR = ${rate} INR`, warn: false };
    }
    return { text: `No rate for ${label} — using nearest: 1 EUR = ${rate} INR`, warn: false };
  }, [inputCurrency, fxMonthKey, fxRateMap, getFxRate]);

  // ── All overheads (unfiltered — filteredCombined handles year/filter) ──
  const yearOverheads = useMemo(() =>
    activeOfficeOverheads,
    [activeOfficeOverheads]
  );

  // ── Bank-imported office expenses from the expenses table ────────────────
  const officeReimbursements = useMemo(() => {
    const source = supabaseExpenses?.expenses || [];
    return source
      .filter(e => e.isOfficeExpense && e.importBatchId)
      .map(e => ({
        id:               `exp-${e.id}`,
        _expenseId:       e.id,
        _isReimbursement: true,
        year:             currentYear,
        category:         e.category || 'Miscellaneous',
        subcategory:      e.description || '—',
        amount:           e.amountInInr ?? e.amount ?? 0,
        amountInInr:      e.amountInInr ?? e.amount ?? 0,
        amountOriginal:   e.amountOriginal ?? e.amount ?? 0,
        currencyOriginal: e.currencyOriginal || 'INR',
        fxRateToInr:      e.fxRateToInr ?? 1,
        officeLocation:   e.officeLocation || 'NL',
        expenseDate:      e.date,
        status:           e.status,
        voucherId:        e.voucherId || null,
        _isLinkedReceipt: false,
      }));
  }, [supabaseExpenses?.expenses, currentYear]);

  // ── Combined data: overheads + reimbursements (all years) ──
  const combinedOverheads = useMemo(() => {
    const tagged = yearOverheads.map(o => ({ ...o, _isReimbursement: false }));
    return [...tagged, ...officeReimbursements];
  }, [yearOverheads, officeReimbursements]);

  // ── Available filter options derived from full data ──
  const availableYears = useMemo(() => {
    const years = new Set();
    const currentYear = new Date().getFullYear();
    for (let y = 2018; y <= currentYear; y++) years.add(String(y));
    yearOverheads.forEach(o => { if (o.year) years.add(String(o.year)); });
    officeReimbursements.forEach(e => { if (e.expenseDate) years.add(e.expenseDate.substring(0, 4)); });
    return [...years].sort((a, b) => b.localeCompare(a));
  }, [yearOverheads, officeReimbursements]);

  const availableCategories = useMemo(() => {
    const cats = new Set(combinedOverheads.map(o => o.category).filter(Boolean));
    return [...cats].sort();
  }, [combinedOverheads]);

  // ── Filtered view ──
  const filteredCombined = useMemo(() => {
    return combinedOverheads.filter(row => {
      const rowYear = row.expenseDate ? row.expenseDate.substring(0, 4) : (row.year ? String(row.year) : null);
      if (filterYear && rowYear !== filterYear) return false;
      if (filterDateFrom && row.expenseDate && row.expenseDate < filterDateFrom) return false;
      if (filterDateTo   && row.expenseDate && row.expenseDate > filterDateTo)   return false;
      if (filterSourceType === 'bank-import' && !row._isReimbursement) return false;
      if (filterSourceType === 'manual'      && row._isReimbursement)  return false;
      if (filterCategory && row.category !== filterCategory)           return false;
      if (filterOffice   && row.officeLocation !== filterOffice)       return false;
      return true;
    });
  }, [combinedOverheads, filterYear, filterDateFrom, filterDateTo, filterSourceType, filterCategory, filterOffice]);

  const categoryGroups = useMemo(() => {
    const groups = {};
    CATEGORY_ORDER.forEach(cat => {
      const items = filteredCombined.filter(o => o.category === cat);
      if (items.length > 0) {
        const catInr = items.filter(o => !o._isLinkedReceipt).reduce((sum, o) => sum + (o.amountInInr ?? o.amount ?? 0), 0);
        groups[cat] = { items, totalInr: catInr };
      }
    });
    filteredCombined.forEach(o => {
      if (!CATEGORY_ORDER.includes(o.category) && o.category) {
        if (!groups[o.category]) groups[o.category] = { items: [], totalInr: 0 };
        if (!groups[o.category].items.find(x => x.id === o.id)) {
          groups[o.category].items.push(o);
          if (!o._isLinkedReceipt) groups[o.category].totalInr += (o.amountInInr ?? o.amount ?? 0);
        }
      }
    });
    return groups;
  }, [filteredCombined]);

  // ── Display helpers ──
  /** Convert an overhead's stored INR amount to display currency */
  const displayAmount = (oh) => {
    const dateRef = oh.expenseDate || `${oh.year}-01-01`;
    const baseInr = oh.amountInInr ?? oh.amount ?? 0;
    return convertMoney(baseInr, 'INR', displayCurrency, dateRef);
  };

  const fmtDisplay = (oh) => formatMoney(displayAmount(oh), displayCurrency, oh?._isReimbursement ? { exact: true } : undefined);

  // Sort helpers
  const handleSortClick = (sortKey) => {
    const newDirection = sortConfig.key === sortKey && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key: sortKey, direction: newDirection });
  };
  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return '';
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  // ── Category drawer toggle ──
  const toggleCategory = useCallback((category) => {
    setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }));
  }, []);

  const expandAll = useCallback(() => {
    const all = {};
    CATEGORY_ORDER.forEach(c => { all[c] = true; });
    setExpandedCategories(all);
  }, []);

  const collapseAll = useCallback(() => {
    setExpandedCategories({});
  }, []);

  // ── Import: parse preview ──
  const handleParseImport = useCallback(() => {
    if (!importText.trim()) return;
    // Steer bank / credit-card statement pastes to the dedicated importer —
    // their columns don't match this "Category, Subcategory, Amount" format,
    // which is how the payee ends up in the category field.
    if (looksLikeBankStatement(importText)) {
      setImportErrors([{ error: 'This looks like a bank or credit-card statement. Import bank exports via Payouts & Vouchers → "Import Statement" (it auto-detects the format and lets you assign a category per transaction). This importer expects manually-prepared rows: Category, Subcategory, Amount [, Office] [, Currency].' }]);
      setImportRows(null);
      return;
    }
    const parsed = parseCsvRows(importText);
    const errors = parsed.filter(r => r.error);
    const valid = parsed.filter(r => !r.error).map((r, idx) => {
      // Snap the free-text category onto the canonical taxonomy where we can;
      // flag the rest so the preview forces a manual choice before import.
      const canonical = canonicalizeOfficeCategory(r.category);
      return {
        ...r,
        category: canonical || r.category,
        _categoryUnknown: !canonical,
        _idx: idx,
        // Apply per-row overrides or fall back to global
        _office: r.office || importOffice,
        _currency: r.currency || importCurrency,
      };
    });
    setImportErrors(errors);
    setImportRows(valid);
  }, [importText, importCurrency, importOffice]);

  // ── Import: update per-row override ──
  const updateImportRow = useCallback((idx, field, value) => {
    setImportRows(prev => prev.map(r => {
      if (r._idx !== idx) return r;
      const next = { ...r, [field]: value };
      // Re-validate the category flag whenever the category is changed.
      if (field === 'category') next._categoryUnknown = !OFFICE_OVERHEAD_CATEGORIES.includes(value);
      return next;
    }));
  }, []);

  // ── Import: remove row from preview ──
  const removeImportRow = useCallback((idx) => {
    setImportRows(prev => prev.filter(r => r._idx !== idx));
  }, []);

  // ── Import: commit rows ──
  const handleImportCommit = useCallback(async () => {
    if (!importRows || importRows.length === 0) return;

    // Guard: never write a non-canonical category. Recognised variants were
    // already snapped on parse; anything still off-list must be fixed via the
    // per-row dropdown before import.
    const badCategoryRows = importRows.filter(r => !OFFICE_OVERHEAD_CATEGORIES.includes(r.category));
    if (badCategoryRows.length > 0) {
      alert(
        `Cannot import: ${badCategoryRows.length} row(s) have a non-standard category. ` +
        `Pick a valid category for:\n` +
        badCategoryRows.map(r => `  • ${r.subcategory || '(no subcategory)'} — "${r.category}"`).join('\n')
      );
      return;
    }

    setImporting(true);

    try {
      const rowsToSave = importRows.map(r => {
        const currency = r._currency || importCurrency;
        const office = r._office || importOffice;
        const amountOriginal = r.amount;
        let fxRateToInr = 1;
        let amountInInr = amountOriginal;

        if (currency === 'EUR') {
          if (!getFxRate) {
            throw new Error('Currency settings not loaded.');
          }
          fxRateToInr = getFxRate(defaultFxMonth + '-01');
          if (!fxRateToInr || fxRateToInr <= 1) {
            throw new Error(`No valid FX rate for ${defaultFxMonth}. Add rates in Settings → FX rates.`);
          }
          amountInInr = Math.round(amountOriginal * fxRateToInr * 100) / 100;
        }

        return {
          year: currentYear,
          category: r.category,
          subcategory: r.subcategory,
          amount: amountInInr,
          officeLocation: office,
          expenseDate: `${defaultFxMonth}-01`,
          amountOriginal,
          currencyOriginal: currency,
          fxRateToInr,
          amountInInr,
          importSource: 'csv-import',
        };
      });

      if (useSupabaseDB) {
        const result = await supabaseOfficeOverheads.bulkCreateOfficeOverheads(rowsToSave);
        if (!result.success) {
          alert('Import error: ' + result.error);
          return;
        }
      } else {
        const newRows = rowsToSave.map((r, i) => ({ id: Date.now() + i, ...r }));
        setOfficeOverheads([...officeOverheads, ...newRows]);
      }

      // Reset import state
      setImportText('');
      setImportRows(null);
      setImportErrors([]);
      setShowImport(false);
    } catch (err) {
      alert('Import error: ' + err.message);
    } finally {
      setImporting(false);
    }
  }, [importRows, importCurrency, importOffice, currentYear, defaultFxMonth, getFxRate, useSupabaseDB, supabaseOfficeOverheads, officeOverheads, setOfficeOverheads]);

  // ── Shared cell renderers ──
  const renderAmountCell = (oh) => {
    const showOrig = oh.currencyOriginal && oh.currencyOriginal !== displayCurrency && oh.amountOriginal != null;
    return (
      <td className="text-right brio-font-medium">
        <span>{fmtDisplay(oh)}</span>
        {showOrig && (
          <div style={{ fontSize: 9, color: '#999', fontStyle: 'italic' }}>
            orig: {oh.currencyOriginal === 'EUR' ? '€' : '₹'}{oh.amountOriginal?.toLocaleString()}
          </div>
        )}
      </td>
    );
  };

  const renderDeleteBtn = (oh) => (
    <td className="text-center">
      <BrioIconButton icon="delete" variant="delete" title="Delete overhead"
        onClick={async () => {
          if (useSupabaseDB) {
            const result = await supabaseOfficeOverheads.deleteOfficeOverhead(oh.id);
            if (!result.success) alert('Error deleting overhead: ' + result.error);
          } else {
            setOfficeOverheads(officeOverheads.filter(o => o.id !== oh.id));
          }
        }}
      />
    </td>
  );

  return (
    <div className="brio-flex brio-flex-col brio-analytics-font" style={{ background: '#fff' }}>
      {/* PAGE HEADER BAND */}
      <div className="brio-pageband brio-pageband--header">
        <BrioFrame>
          <div className="brio-page-header__content">
            <div className="brio-flex brio-items-center brio-gap-xs" style={{ flexWrap: 'nowrap' }}>
              <button
                onClick={() => setShowImport(!showImport)}
                className="brio-btn brio-btn-secondary"
                style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, padding: '5px 10px', flexShrink: 0 }}
                title="Bulk import from CSV"
              >
                <Upload size={13} />
                Import
              </button>
              <BrioSelect value={filterYear} onChange={e => { setFilterYear(e.target.value); if (e.target.value) { setFilterDateFrom(''); setFilterDateTo(''); } }}
                style={{ padding: '4px 8px', fontSize: '11px', minWidth: 70 }}>
                <option value="">All Years</option>
                {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
              </BrioSelect>
              <BrioDatePicker value={filterDateFrom} onChange={v => { setFilterDateFrom(v); if (v) setFilterYear(''); }}
                className="brio-datepicker-trigger--compact" placeholder="From"
                style={{ width: 110, fontSize: '10px' }} clearable />
              <BrioDatePicker value={filterDateTo} onChange={v => { setFilterDateTo(v); if (v) setFilterYear(''); }}
                className="brio-datepicker-trigger--compact" placeholder="To"
                style={{ width: 110, fontSize: '10px' }} clearable />
              <BrioSelect value={filterSourceType} onChange={e => setFilterSourceType(e.target.value)}
                style={{ padding: '4px 8px', fontSize: '11px', minWidth: 110 }}>
                <option value="">All Sources</option>
                <option value="bank-import">Bank Import</option>
                <option value="manual">Manual</option>
              </BrioSelect>
              <BrioSelect value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
                style={{ padding: '4px 8px', fontSize: '11px', minWidth: 120 }}>
                <option value="">All Categories</option>
                {availableCategories.map(c => <option key={c} value={c}>{categoryLabel(c)}</option>)}
              </BrioSelect>
              <BrioSelect value={filterOffice} onChange={e => setFilterOffice(e.target.value)}
                style={{ padding: '4px 8px', fontSize: '11px', minWidth: 70 }}>
                <option value="">All Offices</option>
                <option value="NL">NL</option>
                <option value="IN">IN</option>
              </BrioSelect>
            </div>
          </div>
        </BrioFrame>
      </div>

      {/* CONTENT AREA */}
      <BrioFrame>
        <div className="brio-flex brio-flex-col brio-gap-s" style={{ paddingTop: 'var(--brio-space-s)' }}>

          {/* ── IMPORT PANEL ── */}
          {showImport && (
            <BrioAnalyticsSurface padding="m">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h4 className="brio-analytics-section-title" style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>
                  Bulk Import Office Overheads
                </h4>
                <button onClick={() => { setShowImport(false); setImportRows(null); setImportErrors([]); setImportText(''); }}
                  className="brio-btn brio-btn-secondary" style={{ padding: '2px 6px', lineHeight: 1 }}
                  title="Close import panel">
                  <X size={14} />
                </button>
              </div>

              {/* Global defaults row */}
              <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#666' }}>
                  Default currency:
                  <CurrencyToggle value={importCurrency} onChange={setImportCurrency} />
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#666' }}>
                  Default office:
                  <select value={importOffice} onChange={(e) => setImportOffice(e.target.value)}
                    className="brio-btn brio-btn-secondary" style={{ padding: '2px 8px', fontSize: 11 }}>
                    <option value="NL">NL</option>
                    <option value="IN">IN</option>
                  </select>
                </label>
                <span style={{ fontSize: 10, color: '#999', fontStyle: 'italic' }}>
                  Global defaults apply to all rows. You can override per row in the preview.
                </span>
              </div>

              {/* Text area */}
              <div style={{ marginBottom: 8 }}>
                <textarea
                  value={importText}
                  onChange={(e) => setImportText(e.target.value)}
                  placeholder={`Paste CSV or tab-separated rows:\nCategory, Subcategory, Amount [, Office] [, Currency]\n\nExample:\nPremises, Rent, 120000, NL, EUR\nEnergy & Utilities, Electricity, 45000\nEquipment & IT, Software licenses, 280000, IN`}
                  style={{
                    width: '100%', minHeight: 120, padding: 10, fontSize: 11,
                    fontFamily: "'Raleway', monospace", border: '1px solid var(--brio-border, #d4d0c8)',
                    borderRadius: 4, resize: 'vertical', lineHeight: 1.5,
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: 8, marginBottom: importRows ? 12 : 0 }}>
                <button onClick={handleParseImport} className="brio-btn brio-btn-secondary"
                  style={{ fontSize: 11, padding: '4px 12px' }}>
                  Preview
                </button>
              </div>

              {/* Parse errors */}
              {importErrors.length > 0 && (
                <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 4, padding: 8, marginBottom: 8, fontSize: 10, color: '#991b1b' }}>
                  {importErrors.map((e, i) => <div key={i}>{e.error}</div>)}
                </div>
              )}

              {/* Preview table */}
              {importRows && importRows.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6, color: 'var(--brio-gold, #9F902A)' }}>
                    Preview: {importRows.length} row{importRows.length !== 1 ? 's' : ''} to import for {currentYear}
                  </div>
                  <BrioByCode code={PRESENTATION_CODE}>
                    <BrioTable {...tableProps}>
                      <thead>
                        <tr>
                          <th>Category</th>
                          <th>Subcategory</th>
                          <th className="text-right">Amount</th>
                          <th className="text-center">Currency</th>
                          <th className="text-center">Office</th>
                          <th className="text-center" style={{ width: 32 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {importRows.map(r => (
                          <tr key={r._idx} style={{ background: r._categoryUnknown ? '#fef2f2' : '#fffdf5' }}>
                            <td>
                              <select value={r.category}
                                onChange={(e) => updateImportRow(r._idx, 'category', e.target.value)}
                                className="brio-btn brio-btn-secondary"
                                style={{ width: '100%', padding: '2px 6px', fontSize: 10, borderColor: r._categoryUnknown ? '#dc2626' : undefined }}>
                                {r._categoryUnknown && <option value={r.category}>⚠ {r.category || '(empty)'} — pick one…</option>}
                                {CATEGORY_ORDER.map(c => <option key={c} value={c}>{categoryLabel(c)}</option>)}
                                {/* Keep any non-canonical value that isn't already flagged above */}
                                {!r._categoryUnknown && !CATEGORY_ORDER.includes(r.category) && <option value={r.category}>{categoryLabel(r.category)}</option>}
                              </select>
                              {r._categoryUnknown && (
                                <div style={{ fontSize: 9, color: '#dc2626', marginTop: 2 }}>
                                  Unrecognised category — choose a standard one
                                </div>
                              )}
                            </td>
                            <td style={{ fontSize: 11 }}>{r.subcategory}</td>
                            <td className="text-right" style={{ fontSize: 11, fontWeight: 500 }}>
                              {r.amount.toLocaleString()}
                            </td>
                            <td className="text-center">
                              <CurrencyToggle value={r._currency}
                                onChange={(c) => updateImportRow(r._idx, '_currency', c)} />
                            </td>
                            <td className="text-center">
                              <select value={r._office}
                                onChange={(e) => updateImportRow(r._idx, '_office', e.target.value)}
                                className="brio-btn brio-btn-secondary"
                                style={{ padding: '2px 6px', fontSize: 10 }}>
                                <option value="NL">NL</option>
                                <option value="IN">IN</option>
                              </select>
                            </td>
                            <td className="text-center">
                              <button onClick={() => removeImportRow(r._idx)}
                                style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#999', padding: 2 }}
                                title="Remove row">
                                <X size={12} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </BrioTable>
                  </BrioByCode>

                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button onClick={handleImportCommit} disabled={importing}
                      className="brio-btn brio-btn-primary"
                      style={{ fontSize: 11, padding: '5px 16px' }}>
                      {importing ? 'Importing...' : `Import ${importRows.length} row${importRows.length !== 1 ? 's' : ''}`}
                    </button>
                    <button onClick={() => { setImportRows(null); setImportErrors([]); }}
                      className="brio-btn brio-btn-secondary"
                      style={{ fontSize: 11, padding: '5px 12px' }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {importRows && importRows.length === 0 && importErrors.length === 0 && (
                <div style={{ fontSize: 11, color: '#999', fontStyle: 'italic' }}>No valid rows found. Check your input format.</div>
              )}
            </BrioAnalyticsSurface>
          )}

          {(() => {
            // ── Totals: sum amount_in_inr, convert for display ──
            // Linked receipts are shown in the table but excluded from totals
            const countable = filteredCombined.filter(o => !o._isLinkedReceipt);
            const totalInr = countable.reduce((sum, o) => sum + (o.amountInInr ?? o.amount ?? 0), 0);
            const totalDisplay = convertMoney(totalInr, 'INR', displayCurrency, `${currentYear}-01-01`);

            // ── Office-specific totals ──
            const rotterdamInr = countable.filter(o => (o.officeLocation || 'NL') === 'NL').reduce((sum, o) => sum + (o.amountInInr ?? o.amount ?? 0), 0);
            const mumbaiInr = countable.filter(o => (o.officeLocation || 'NL') === 'IN').reduce((sum, o) => sum + (o.amountInInr ?? o.amount ?? 0), 0);
            const rotterdamDisplay = convertMoney(rotterdamInr, 'INR', displayCurrency, `${currentYear}-01-01`);
            const mumbaiDisplay = convertMoney(mumbaiInr, 'INR', displayCurrency, `${currentYear}-01-01`);

            // Sort
            const sortedOverheads = [...filteredCombined].sort((a, b) => {
              let valA, valB;
              switch(sortConfig.key) {
                case 'category':
                  valA = (a.category || '').toLowerCase();
                  valB = (b.category || '').toLowerCase();
                  return sortConfig.direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
                case 'subcategory':
                  valA = (a.subcategory || '').toLowerCase();
                  valB = (b.subcategory || '').toLowerCase();
                  return sortConfig.direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
                case 'amount':
                  valA = a.amountInInr ?? a.amount ?? 0;
                  valB = b.amountInInr ?? b.amount ?? 0;
                  return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
                case 'office':
                  valA = (a.officeLocation || 'NL').toLowerCase();
                  valB = (b.officeLocation || 'NL').toLowerCase();
                  return sortConfig.direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
                case 'date':
                  valA = a.expenseDate || '';
                  valB = b.expenseDate || '';
                  return sortConfig.direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
                default:
                  return 0;
              }
            });

            // Include any non-canonical categories present in the data so that
            // orphaned-category rows are never silently hidden from the grouped
            // view — they are still summed into the totals above. Canonical
            // categories first, then any extras alphabetically.
            const extraCategories = Object.keys(categoryGroups)
              .filter(cat => !CATEGORY_ORDER.includes(cat))
              .sort((a, b) => a.localeCompare(b));
            const orderedCategories = [...CATEGORY_ORDER, ...extraCategories];
            const sortedCategories = sortConfig.key === 'category'
              ? (sortConfig.direction === 'asc' ? orderedCategories : [...orderedCategories].reverse())
              : orderedCategories;

            // For non-category sort, show flat table; for category sort, show drawers
            const useCategoryDrawers = sortConfig.key === 'category';

            return (
              <>
                {/* Total Summary */}
                <div style={{ background: 'var(--brio-gold-light)', border: '1px solid var(--brio-gold)', padding: '16px' }}>
                  <div className="brio-flex brio-justify-between brio-items-center" style={{ marginBottom: 8 }}>
                    <span className="brio-analytics-muted">Total for {currentYear} – NL</span>
                    <span style={{ fontSize: '16px', fontWeight: 500 }}>{formatMoney(rotterdamDisplay, displayCurrency)}</span>
                  </div>
                  <div className="brio-flex brio-justify-between brio-items-center" style={{ marginBottom: 8 }}>
                    <span className="brio-analytics-muted">Total for {currentYear} – IN</span>
                    <span style={{ fontSize: '16px', fontWeight: 500 }}>{formatMoney(mumbaiDisplay, displayCurrency)}</span>
                  </div>
                  <div className="brio-flex brio-justify-between brio-items-center" style={{ borderTop: '1px solid var(--brio-gold)', paddingTop: 8 }}>
                    <span className="brio-analytics-muted">Total for {currentYear}</span>
                    <span style={{ fontSize: '18px', fontWeight: 600 }}>{formatMoney(totalDisplay, displayCurrency)}</span>
                  </div>
                </div>

                {/* Overheads Table */}
                <BrioAnalyticsSurface padding="m">
                  {/* Expand/Collapse controls (shown when in category sort mode) */}
                  {useCategoryDrawers && filteredCombined.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <button onClick={expandAll} className="brio-btn brio-btn-secondary"
                        style={{ fontSize: 10, padding: '2px 8px' }}>
                        Expand all
                      </button>
                      <button onClick={collapseAll} className="brio-btn brio-btn-secondary"
                        style={{ fontSize: 10, padding: '2px 8px' }}>
                        Collapse all
                      </button>
                    </div>
                  )}

                  <BrioByCode code={PRESENTATION_CODE}>
                    <BrioTable {...tableProps}>
                      <thead>
                        <tr>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSortClick('category')}>
                            Category{getSortIndicator('category')}
                          </th>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSortClick('subcategory')}>
                            Subcategory{getSortIndicator('subcategory')}
                          </th>
                          <th style={{ cursor: 'pointer', width: 90 }} onClick={() => handleSortClick('date')}>
                            Date{getSortIndicator('date')}
                          </th>
                          <th className="text-right" style={{ cursor: 'pointer' }} onClick={() => handleSortClick('amount')}>
                            Amount ({displaySymbol}/year){getSortIndicator('amount')}
                          </th>
                          <th className="text-center" style={{ cursor: 'pointer' }} onClick={() => handleSortClick('office')}>
                            Office{getSortIndicator('office')}
                          </th>
                          <th className="text-center" style={{ width: 70 }}>Input Source</th>
                          <th className="text-center col-actions">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {!useCategoryDrawers ? (
                          /* ── Flat sort mode ── */
                          sortedOverheads.map(oh => (
                            <tr key={oh.id} style={oh._isLinkedReceipt ? { opacity: 0.55 } : undefined}>
                              <td className="brio-font-medium">{categoryLabel(oh.category)}</td>
                              <td className="brio-text-grey">{oh.subcategory}</td>
                              <td style={{ fontSize: 10, whiteSpace: 'nowrap', color: '#888' }}>{oh.expenseDate || '—'}</td>
                              {renderAmountCell(oh)}
                              <td className="text-center brio-text-grey">{oh.officeLocation || 'NL'}</td>
                              <td className="text-center">
                                {oh._isLinkedReceipt ? (
                                  <span style={{ fontSize: 8, color: 'var(--brio-teal)', fontStyle: 'italic' }}>Linked</span>
                                ) : oh._isReimbursement ? (
                                  <span className="brio-badge" style={{ fontSize: 9, padding: '1px 5px', backgroundColor: 'var(--brio-teal-light)', color: 'var(--brio-teal)' }}>Receipt</span>
                                ) : oh.importSource && oh.importSource.startsWith('bank-import') ? (
                                  <span style={{ fontSize: 9, color: 'var(--brio-gold)' }}>Bank Import</span>
                                ) : (
                                  <span style={{ fontSize: 9, color: 'var(--brio-grey)' }}>Manual</span>
                                )}
                              </td>
                              {oh._isReimbursement ? <td></td> : renderDeleteBtn(oh)}
                            </tr>
                          ))
                        ) : (
                          /* ── Category drawer mode ── */
                          sortedCategories.map(category => {
                            const group = categoryGroups[category];
                            if (!group) return null;
                            const isExpanded = !!expandedCategories[category];
                            const catTotalDisplay = convertMoney(group.totalInr, 'INR', displayCurrency, `${currentYear}-01-01`);

                            return (
                              <React.Fragment key={category}>
                                {/* Category header row (clickable drawer toggle) */}
                                <tr
                                  onClick={() => toggleCategory(category)}
                                  style={{
                                    cursor: 'pointer',
                                    background: 'var(--brio-gold-light, #faf8f0)',
                                    borderBottom: isExpanded ? 'none' : undefined,
                                  }}
                                >
                                  <td className="brio-font-medium" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    {isExpanded
                                      ? <ChevronDown size={13} style={{ flexShrink: 0, color: 'var(--brio-gold, #9F902A)' }} />
                                      : <ChevronRight size={13} style={{ flexShrink: 0, color: '#999' }} />
                                    }
                                    <span>{categoryLabel(category)}</span>
                                    <span style={{ fontSize: 10, color: '#999', fontWeight: 400, marginLeft: 4 }}>
                                      ({group.items.length})
                                    </span>
                                    {!CATEGORY_ORDER.includes(category) && (
                                      <span
                                        title="Non-standard category — not in the office overhead taxonomy. These rows count toward totals; re-categorise them to a standard category."
                                        style={{ fontSize: 8, marginLeft: 6, padding: '0 4px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--brio-gold, #9F902A)', border: '1px solid var(--brio-gold, #9F902A)', borderRadius: 3 }}
                                      >
                                        non-standard
                                      </span>
                                    )}
                                  </td>
                                  <td></td>
                                  <td></td>
                                  <td className="text-right brio-font-medium">
                                    {formatMoney(catTotalDisplay, displayCurrency)}
                                  </td>
                                  <td></td>
                                  <td></td>
                                  <td></td>
                                </tr>

                                {/* Expanded rows */}
                                {isExpanded && group.items.map(oh => (
                                  <tr key={oh.id} style={{ background: '#fff', ...(oh._isLinkedReceipt ? { opacity: 0.55 } : {}) }}>
                                    <td style={{ paddingLeft: 28 }}></td>
                                    <td className="brio-text-grey">{oh.subcategory}</td>
                                    <td style={{ fontSize: 10, whiteSpace: 'nowrap', color: '#888' }}>{oh.expenseDate || '—'}</td>
                                    {renderAmountCell(oh)}
                                    <td className="text-center brio-text-grey">{oh.officeLocation || 'NL'}</td>
                                    <td className="text-center">
                                      {oh._isLinkedReceipt ? (
                                        <span style={{ fontSize: 8, color: 'var(--brio-teal)', fontStyle: 'italic' }}>Linked</span>
                                      ) : oh._isReimbursement ? (
                                        <span className="brio-badge" style={{ fontSize: 9, padding: '1px 5px', backgroundColor: 'var(--brio-teal-light)', color: 'var(--brio-teal)' }}>Receipt</span>
                                      ) : oh.importSource && oh.importSource.startsWith('bank-import') ? (
                                        <span style={{ fontSize: 9, color: 'var(--brio-gold)' }}>Bank Import</span>
                                      ) : (
                                        <span style={{ fontSize: 9, color: 'var(--brio-grey)' }}>Manual</span>
                                      )}
                                    </td>
                                    {oh._isReimbursement ? <td></td> : renderDeleteBtn(oh)}
                                  </tr>
                                ))}
                              </React.Fragment>
                            );
                          })
                        )}

                        {/* ── Add new row ── */}
                        <tr style={{ backgroundColor: 'var(--brio-gold-light)' }}>
                          <td>
                            <select
                              value={form.newOverheadCategory || ''}
                              onChange={(e) => setForm({...form, newOverheadCategory: e.target.value})}
                              className="brio-btn brio-btn-secondary"
                              style={{ width: '100%', padding: '4px 8px', fontSize: '11px' }}
                            >
                              <option value="">Select...</option>
                              {CATEGORY_ORDER.map(c => <option key={c} value={c}>{categoryLabel(c)}</option>)}
                            </select>
                          </td>
                          <td>
                            <input
                              type="text"
                              value={form.newOverheadSubcat || ''}
                              onChange={(e) => setForm({...form, newOverheadSubcat: e.target.value})}
                              placeholder="e.g., Rent, Electricity"
                              className="brio-btn brio-btn-secondary"
                              style={{ width: '100%', padding: '4px 8px', fontSize: '11px' }}
                            />
                          </td>
                          <td>
                            <input
                              type="date"
                              value={form.newOverheadDate || ''}
                              onChange={(e) => setForm({...form, newOverheadDate: e.target.value})}
                              className="brio-btn brio-btn-secondary"
                              style={{ width: '100%', padding: '4px 8px', fontSize: '11px' }}
                            />
                          </td>
                          <td>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                <input
                                  type="number"
                                  value={form.newOverheadAmount || ''}
                                  onChange={(e) => setForm({...form, newOverheadAmount: e.target.value})}
                                  placeholder="0"
                                  className="brio-btn brio-btn-secondary"
                                  style={{ flex: 1, minWidth: 0, padding: '4px 8px', fontSize: '11px', textAlign: 'right' }}
                                />
                                <CurrencyToggle
                                  value={inputCurrency}
                                  onChange={(c) => setForm({...form, newOverheadCurrency: c})}
                                />
                              </div>
                              {/* Month picker for FX rate (shown when EUR) */}
                              {inputCurrency === 'EUR' && (
                                <BrioMonthPicker
                                  value={fxMonthKey}
                                  onChange={(v) => setForm({...form, newOverheadFxMonth: v})}
                                  style={{ width: '100%' }}
                                  title="Month for FX rate lookup"
                                />
                              )}
                              {/* FX helper text (monthly average) */}
                              {fxHelper && (
                                <span style={{ fontSize: 9, color: fxHelper.warn ? '#c0392b' : '#999', fontStyle: 'italic' }}>
                                  {fxHelper.warn ? '\u26A0 ' : ''}{fxHelper.text}
                                </span>
                              )}
                            </div>
                          </td>
                          <td>
                            <select
                              value={form.newOverheadLocation || 'NL'}
                              onChange={(e) => setForm({...form, newOverheadLocation: e.target.value})}
                              className="brio-btn brio-btn-secondary"
                              style={{ width: '100%', padding: '4px 8px', fontSize: '11px' }}
                            >
                              <option value="NL">NL</option>
                              <option value="IN">IN</option>
                            </select>
                          </td>
                          <td></td>{/* Source column — empty for new row */}
                          <td className="text-center">
                            <button
                              onClick={async () => {
                                if (!form.newOverheadCategory || !form.newOverheadSubcat || !form.newOverheadAmount) {
                                  alert('Please fill all fields');
                                  return;
                                }

                                const amountOriginal = parseFloat(form.newOverheadAmount);
                                const currencyOriginal = inputCurrency;

                                // ── Compute amount_in_inr using monthly average FX ──
                                let fxRateToInr = 1;
                                let amountInInr = amountOriginal;

                                if (currencyOriginal === 'EUR') {
                                  if (!getFxRate) {
                                    alert('Currency settings not loaded. Please wait and try again.');
                                    return;
                                  }
                                  fxRateToInr = getFxRate(fxMonthKey + '-01');
                                  if (!fxRateToInr || fxRateToInr <= 1) {
                                    alert(`No valid FX rate available for ${fxMonthKey}. Please add rates in Settings → FX rates.`);
                                    return;
                                  }
                                  amountInInr = Math.round(amountOriginal * fxRateToInr * 100) / 100;
                                }

                                const newOverhead = {
                                  year: currentYear,
                                  category: form.newOverheadCategory,
                                  subcategory: form.newOverheadSubcat,
                                  amount: amountInInr,               // legacy column = amount_in_inr
                                  officeLocation: form.newOverheadLocation || 'NL',
                                  expenseDate: form.newOverheadDate || `${fxMonthKey}-01`,
                                  amountOriginal,
                                  currencyOriginal,
                                  fxRateToInr,
                                  amountInInr,
                                };

                                if (useSupabaseDB) {
                                  const result = await supabaseOfficeOverheads.createOfficeOverhead(newOverhead);
                                  if (!result.success) {
                                    alert('Error creating overhead: ' + result.error);
                                    return;
                                  }
                                } else {
                                  setOfficeOverheads([...officeOverheads, { id: Date.now(), ...newOverhead }]);
                                }

                                setForm({
                                  ...form,
                                  newOverheadCategory: '',
                                  newOverheadSubcat: '',
                                  newOverheadAmount: '',
                                  newOverheadLocation: '',
                                  newOverheadCurrency: 'INR',
                                  newOverheadFxMonth: '',
                                  newOverheadDate: '',
                                });
                              }}
                              className="brio-btn brio-btn-primary"
                              style={{ padding: '4px 12px', fontSize: '11px' }}
                            >
                              Add
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </BrioTable>
                  </BrioByCode>

                  {filteredCombined.length === 0 && (
                    <div className="brio-text-center brio-analytics-muted" style={{ padding: '32px 0' }}>
                      No overheads found{filterYear ? ` for ${filterYear}` : ''}. Adjust filters or use the table above to add yearly expenses.
                    </div>
                  )}
                </BrioAnalyticsSurface>
              </>
            );
          })()}
        </div>
      </BrioFrame>
    </div>
  );
}
